import * as React from "react";

import type {
  Filter,
  FilterDefinition,
} from "@/shared/components/ResourceView";
import {
  coreSessionFilters,
  sessionDatasetIdFilter,
  SessionsList,
} from "@/shared/components/sessions";

import { useDeviceContext } from "../Device/useDeviceContext";

import styles from "./DeviceSessions.module.css";

/**
 * Rows the filter menu offers on the device-scoped Sessions tab. The device
 * id is the server-side scope, so the picker omits it and offers only the
 * dataset id as the useful cross-aggregate narrowing axis.
 */
const deviceSessionsFilterDefinitions: FilterDefinition[] = [
  ...coreSessionFilters,
  sessionDatasetIdFilter,
];

/**
 * Body of the Sessions tab on the device-detail page. Renders the shared
 * `<SessionsList>` scoped to the current device; the scope is enforced
 * server-side via the `device.device_id` cross-aggregate filter and cannot
 * be widened by the user.
 *
 * Mounted only when `featureFlagRoute` in `DevicesRoutes` admits
 * `/devices/:deviceId/sessions` under the `release-sessions` flag; with
 * the flag off the route redirects to the device's Datasets tab.
 */
export function DeviceSessions() {
  const { deviceId } = useDeviceContext();

  const scope = React.useMemo<Filter[]>(
    () => [
      {
        type: "string",
        field: "device.device_id",
        comparator: "EQUALS",
        values: [deviceId],
      },
    ],
    [deviceId],
  );

  return (
    <SessionsList
      className={styles.sessionList}
      scope={scope}
      filterDefinitions={deviceSessionsFilterDefinitions}
      noRowsContent="No sessions for this device"
    />
  );
}
